import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { computeDocNavLayout, type DocNavLayout, type Rect } from '../../lib/docnav';

/**
 * Measurement for the documentation navigation band — the input side of `lib/docnav.ts`.
 *
 * The split is the point, and it is the same one the map needs: this hook does nothing but
 * READ rectangles and hand them to a pure function; the pure function decides; React writes.
 * The vanilla band interleaved all three (write a margin, read a rect, write the margin back,
 * read every rect again to draw wires), which is why it could only be verified by
 * photographing it.
 *
 * A `ResizeObserver` is what makes reading-without-writing sufficient. The band changes size
 * for reasons React never hears about — a web font arriving, a doc title landing from the
 * server and re-wrapping a pill, the window resizing — and the original answered that with a
 * `window.resize` listener that caught only the last of the three.
 *
 * ELEMENTS ARE FOUND BY SELECTOR rather than by threaded refs. Six refs would have to cross
 * three components to reach here, and the selectors are the same ones the vanilla code used,
 * so the reading is greppable against the original. Measurement by query is fine; it is
 * measurement inside a WRITE loop that is not.
 *
 * CONVERGENCE. Each pass reports the offset it currently has applied, so the pure function
 * returns an absolute answer rather than a delta, and the second pass finds nothing to change.
 * `MAX_PASSES` is a hang guard, not a schedule: legitimate settling takes at most three.
 */

const EMPTY: DocNavLayout = {
  width: 0,
  height: 0,
  childrenOffset: 0,
  parentDocClearance: null,
  wires: [],
};

/** Value equality, so a re-measure that found nothing does not cause a render. */
function same(a: DocNavLayout, b: DocNavLayout): boolean {
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.childrenOffset === b.childrenOffset &&
    a.parentDocClearance === b.parentDocClearance &&
    a.wires.length === b.wires.length &&
    a.wires.every((w, i) => w.key === b.wires[i].key && w.d === b.wires[i].d && w.color === b.wires[i].color)
  );
}

/**
 * Upper bound on measure→render→measure cycles between content changes.
 *
 * Two is the normal count and three is the worst legitimate one. Anything beyond that is
 * sub-pixel jitter feeding itself, and a browser tab that never stops re-rendering is a worse
 * failure than a wire being half a pixel out.
 */
const MAX_PASSES = 8;

const toRect = (el: Element | null): Rect | null => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, right: r.right, top: r.top, bottom: r.bottom };
};

export interface DocNavLayoutOptions {
  stage: RefObject<HTMLElement | null>;
  grid: RefObject<HTMLElement | null>;
  /** False when there is no focus node — the band is collapsed and there is nothing to place. */
  active: boolean;
  currentId: string | null;
  colorOf: (id: string | null) => string;
  /** Changes whenever the band's CONTENT changes. Resets the convergence budget. */
  navKey: string;
}

export function useDocNavLayout({
  stage,
  grid,
  active,
  currentId,
  colorOf,
  navKey,
}: DocNavLayoutOptions): DocNavLayout {
  const [layout, setLayout] = useState<DocNavLayout>(EMPTY);
  // Read inside `measure` without making it a dependency: the applied offset is an input to
  // the next measurement, not a reason to re-create the callback.
  const applied = useRef(layout);
  applied.current = layout;
  const passes = useRef(0);

  const measure = useCallback(() => {
    const st = stage.current;
    const gd = grid.current;
    if (!st || !gd || !active) {
      if (!same(applied.current, EMPTY)) setLayout(EMPTY);
      return;
    }
    const stageRect = toRect(st);
    if (!stageRect) return;

    const parentDocs = gd.querySelector('.docnav-parentgroup .docnav-doccol');
    const children: { id: string; rect: Rect }[] = [];
    for (const item of gd.querySelectorAll('#docnav-children .docnav-childitem')) {
      const id = (item as HTMLElement).dataset.childId;
      const rect = toRect(item.querySelector('.docnav-node'));
      if (id && rect) children.push({ id, rect });
    }

    const next = computeDocNavLayout(
      {
        stage: stageRect,
        current: toRect(gd.querySelector('#docnav-current > .docnav-node')),
        parent: toRect(gd.querySelector('#docnav-parent > .docnav-node')),
        page: toRect(gd.querySelector('.docframe-wrap')),
        children,
        // An empty column has nothing hanging into the row below, so there is nothing to
        // clear — the same `childElementCount` test the original made.
        parentDocs: parentDocs?.childElementCount ? toRect(parentDocs) : null,
        logs: toRect(gd.querySelector('#doclogs')),
        appliedChildrenOffset: applied.current.childrenOffset,
      },
      colorOf,
      currentId,
    );

    if (same(next, applied.current)) return;
    if (passes.current >= MAX_PASSES) return;
    passes.current += 1;
    setLayout(next);
  }, [stage, grid, active, colorOf, currentId]);

  useEffect(() => {
    passes.current = 0;
  }, [navKey]);

  // The observer calls through a ref rather than closing over `measure`, so it is created once
  // per band rather than once per render. `measure` changes whenever the map snapshot does —
  // which is every render() the vanilla app makes, ~50 call sites — and a ResizeObserver
  // rebuilt that often would spend more time re-observing than measuring.
  const latest = useRef(measure);
  latest.current = measure;

  // After EVERY render, because everything the band shows can change its size: a pill
  // relabelled by a title lookup, a child list replaced, the log panel appearing and taking
  // a column. Cheap — a handful of rects, and it returns without a render when nothing moved.
  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const st = stage.current;
    const gd = grid.current;
    if (!st || !gd || !active) return;
    const ro = new ResizeObserver(() => latest.current());
    const targets = [
      st,
      gd.querySelector('#docnav-parent'),
      gd.querySelector('#docnav-current'),
      gd.querySelector('#docnav-children'),
      gd.querySelector('.docframe-wrap'),
    ];
    // Deliberately NOT #doclogs: its padding-top is what this computes, so observing its
    // border box would be watching our own output.
    for (const t of targets) if (t) ro.observe(t);
    return () => ro.disconnect();
  }, [stage, grid, active, navKey]);

  return layout;
}
